import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Play, Clock, CheckCircle2 } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function CursosGratuitos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [marking, setMarking] = useState(null);
  const [area, setArea] = useState(user?.profile === "licitacoes" ? "licitacoes" : "legislativo");

  const loadProgress = () => {
    api.get("/free-courses/me/progress").then(r => {
      const m = {};
      (r.data || []).forEach(p => { m[p.course_id] = p; });
      setProgressMap(m);
    }).catch(() => {});
  };

  useEffect(() => {
    const params = {};
    if (user?.profile !== "ambos") params.area = user?.profile;
    else params.area = area;
    api.get("/free-courses", { params }).then(r => setItems(r.data || [])).catch(() => {});
    loadProgress();
  }, [user?.profile, area]);

  const markAsWatched = async (e, courseId) => {
    e.preventDefault();
    e.stopPropagation();
    if (marking === courseId) return;
    setMarking(courseId);
    try {
      const current = progressMap[courseId];
      const watched = current?.watched_seconds || 0;
      await api.post(`/free-courses/${courseId}/progress`, { watched_seconds: watched, completed: true });
      toast.success("Aula marcada como assistida!");
      loadProgress();
    } catch {
      toast.error("Não foi possível marcar a aula.");
    } finally {
      setMarking(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="aulas-gratuitas-page">
      <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Aulas Gratuitas</h1>
      {user?.profile === "ambos" && (
        <Tabs value={area} onValueChange={setArea}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="legislativo" data-testid="tab-legislativo">Legislativo</TabsTrigger>
            <TabsTrigger value="licitacoes" data-testid="tab-licitacoes">Licitações</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {items.length === 0 ? (
        <EmptyState title="Nenhuma aula gratuita por aqui" description="Logo teremos novos conteúdos para você." />
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="free-courses-list">
          {items.map(c => {
            const prog = progressMap[c.id];
            const completed = !!prog?.completed;
            const totalSec = (c.duration_minutes || 0) * 60;
            const pct = totalSec > 0 && prog ? Math.min(100, Math.round((prog.watched_seconds / totalSec) * 100)) : 0;
            return (
              <Card key={c.id} className="rounded-xl overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col" data-testid={`free-course-${c.id}`}>
                <Link to={`/aulas-gratuitas/${c.id}`} className="block">
                  {c.thumbnail_url ? (
                    <div className="relative aspect-video">
                      <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-10 h-10 rounded-full bg-white/95 text-[hsl(var(--primary))] flex items-center justify-center">
                          <Play className="w-5 h-5 fill-current" />
                        </div>
                      </div>
                      {completed && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-medium flex items-center gap-1 shadow">
                          <CheckCircle2 className="w-3 h-3" /> Assistida
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))]" />
                  )}
                </Link>
                <CardContent className="p-3 flex-1 flex flex-col">
                  <Badge variant="secondary" className="text-[10px] mb-1 capitalize w-fit">{c.category || c.area}</Badge>
                  <Link to={`/aulas-gratuitas/${c.id}`} className="block">
                    <h3 className="font-display font-medium text-sm text-[hsl(var(--primary))] line-clamp-2">{c.title}</h3>
                  </Link>
                  {c.duration_minutes > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration_minutes} min</p>
                  )}
                  {prog && !completed && pct > 0 && (
                    <div className="mt-2 h-1 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  <div className="mt-auto pt-2">
                    {completed ? (
                      <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50 w-full justify-center py-1">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Já assistida
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-[11px] h-7"
                        onClick={(e) => markAsWatched(e, c.id)}
                        disabled={marking === c.id}
                        data-testid={`mark-watched-${c.id}`}
                      >
                        {marking === c.id ? "Marcando..." : "Marcar como assistida"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
